import React, { useState } from 'react';

// Additional UI components needed for RBAC
export const Table: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = '' }) => (
  <table className={`w-full border-collapse ${className}`}>{children}</table>
);

export const TableHead: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = '' }) => (
  <thead className={`bg-background-card ${className}`}>{children}</thead>
);

export const TableBody: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = '' }) => (
  <tbody className={className}>{children}</tbody>
);

export const TableRow: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = '' }) => (
  <tr className={`border-b border-border-dark ${className}`}>{children}</tr>
);

export const TableCell: React.FC<{ children: React.ReactNode, className?: string }> = ({ children, className = '' }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);

// Tabs components
interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({ value, onValueChange, children, className = '' }) => (
  <div className={className}>
    {children}
  </div>
);

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
}

export const TabsList: React.FC<TabsListProps> = ({ children, className = '' }) => (
  <div className={`flex border-b border-border-dark ${className}`}>
    {children}
  </div>
);

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  active?: boolean;
}

export const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, children, className = '', active }) => (
  <button 
    className={`px-4 py-2 font-medium transition-colors
      ${active ? 'border-b-2 border-primary text-primary' : 'text-gray-400 hover:text-gray-600'}
      ${className}`}
  >
    {children}
  </button>
);

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export const TabsContent: React.FC<TabsContentProps> = ({ value, children, className = '' }) => (
  <div className={className}>
    {children}
  </div>
);

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
}

export const Spinner: React.FC<SpinnerProps> = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  };
  
  return (
    <div className={`animate-spin rounded-full border-2 border-t-transparent border-primary ${sizeClasses[size]}`}></div>
  );
};

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline' | 'outline-danger' | 'glass' | 'gradient-primary' | 'gradient-secondary';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  title?: string;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
  type = 'button',
  title,
  isLoading = false,
  leftIcon,
  rightIcon
}) => {
  // Base classes
  const baseClasses = 'btn relative overflow-hidden transition-all duration-300 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background-dark transform hover:-translate-y-0.5 active:translate-y-0 shadow-md';
  
  // Get variant classes
  const getVariantClasses = (variant: ButtonProps['variant']) => {
    switch (variant) {
      case 'primary':
        return 'bg-primary hover:bg-primary-hover text-white border-transparent';
      case 'secondary':
        return 'bg-secondary hover:bg-secondary-hover text-white border-transparent';
      case 'danger':
        return 'bg-status-danger hover:bg-status-danger/80 text-white border-transparent';
      case 'success':
        return 'bg-status-success hover:bg-status-success/80 text-white border-transparent';
      case 'outline':
        return 'bg-transparent hover:bg-background-hover text-primary border border-border-dark';
      case 'outline-danger':
        return 'bg-transparent hover:bg-status-danger/10 text-status-danger border border-status-danger/50';
      case 'glass':
        return 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/20';
      case 'gradient-primary':
        return 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-transparent';
      case 'gradient-secondary':
        return 'bg-opacity-50 bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600';
      default:
        return 'bg-primary hover:bg-primary-hover text-white border-transparent';
    }
  };
  
  // Size classes
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs rounded',
    sm: 'px-3 py-1.5 text-xs rounded-md',
    md: 'px-4 py-2 text-sm rounded-lg',
    lg: 'px-6 py-3 text-base rounded-xl'
  };
  
  // Disabled and loading classes
  const stateClasses = (disabled || isLoading) ? 'opacity-70 cursor-not-allowed transform-none hover:shadow-none' : '';
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      title={title}
      className={`
        ${baseClasses}
        ${getVariantClasses(variant)}
        ${sizeClasses[size]}
        ${stateClasses}
        ${className}
        ${leftIcon || rightIcon || isLoading ? 'flex items-center gap-2 justify-center' : ''}
      `}
    >
      {isLoading && (
        <div className="mr-2 w-4 h-4 rounded-full border-2 border-t-transparent border-white animate-spin"></div>
      )}
      
      {!isLoading && leftIcon && <span className="btn-icon">{leftIcon}</span>}
      <span className="relative">{children}</span>
      {!isLoading && rightIcon && <span className="btn-icon">{rightIcon}</span>}
    </button>
  );
};

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'outline';
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  className = ''
}) => {
  const variantClasses = {
    default: 'bg-gray-100 text-gray-800',
    primary: 'bg-primary/10 text-primary',
    secondary: 'bg-secondary/10 text-secondary',
    success: 'bg-status-success/10 text-status-success',
    warning: 'bg-status-warning/10 text-status-warning',
    danger: 'bg-status-danger/10 text-status-danger',
    outline: 'bg-transparent border border-border-dark text-gray-400'
  };
  
  return (
    <span className={`
      inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium
      ${variantClasses[variant]}
      ${className}
    `}>
      {children}
    </span>
  );
};

interface InputProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  type?: string;
}

export const Input: React.FC<InputProps> = ({ 
  id, 
  name, 
  value, 
  onChange, 
  placeholder, 
  className = '',
  disabled = false,
  type = 'text'
}) => (
  <input
    id={id}
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    type={type}
    className={`w-full px-3 py-2 border border-border-dark rounded-md bg-background-card text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary ${disabled ? 'bg-background-hover cursor-not-allowed' : ''} ${className}`}
  />
);

interface LabelProps {
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}

export const Label: React.FC<LabelProps> = ({ 
  htmlFor, 
  children, 
  className = '' 
}) => (
  <label htmlFor={htmlFor} className={`block text-sm font-medium text-gray-300 mb-1 ${className}`}>
    {children}
  </label>
);

interface TextareaProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  className?: string;
  rows?: number;
}

export const Textarea: React.FC<TextareaProps> = ({ 
  id, 
  name, 
  value, 
  onChange, 
  placeholder, 
  className = '',
  rows = 3
}) => (
  <textarea
    id={id}
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    className={`w-full px-3 py-2 border border-border-dark rounded-md bg-background-card text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary ${className}`}
  />
);

interface SelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

export const Select: React.FC<SelectProps> = ({ 
  value,
  onValueChange,
  children,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleSelect = (value: string) => {
    if (onValueChange) {
      onValueChange(value);
    }
    setIsOpen(false);
  };
  
  // Find the selected item's text to display
  let selectedItemText: React.ReactNode = null;
  
  // Manually traverse the children to find the selected item
  React.Children.forEach(children, (contentChild) => {
    if (React.isValidElement(contentChild) && contentChild.type === SelectContent) {
      const content = contentChild as React.ReactElement<SelectContentProps>;
      React.Children.forEach(content.props.children, (itemChild) => {
        if (
          React.isValidElement(itemChild) && 
          itemChild.type === SelectItem
        ) {
          const item = itemChild as React.ReactElement<SelectItemProps>;
          if (item.props.value === value) {
            selectedItemText = item.props.children;
          }
        }
      });
    }
  });
  
  return (
    <div className="relative">
      <div 
        onClick={() => !disabled && setIsOpen(!isOpen)} 
        className={`flex items-center justify-between px-3 py-2 border border-border-dark rounded-md cursor-pointer bg-background-card text-gray-300 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        {React.Children.map(children, child => {
          if (React.isValidElement(child) && child.type === SelectTrigger) {
            return React.cloneElement(child as React.ReactElement<SelectTriggerProps>, {
              value,
              selectedText: selectedItemText
            });
          }
          return null;
        })}
      </div>
      
      {isOpen && (
        <div className="absolute w-full mt-1 bg-background-card border border-border-dark rounded-md shadow-lg z-50">
          {React.Children.map(children, child => {
            if (React.isValidElement(child) && child.type === SelectContent) {
              return React.cloneElement(child as React.ReactElement<SelectContentProps>, {
                onSelect: handleSelect,
                value: value
              });
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};

interface SelectTriggerProps {
  children: React.ReactNode;
  className?: string;
  value?: string;
  selectedText?: React.ReactNode;
}

export const SelectTrigger: React.FC<SelectTriggerProps> = ({ children, className = '', value, selectedText }) => (
  <div className={`flex items-center justify-between w-full ${className}`}>
    <div className="flex-1">
      {React.Children.map(children, child => {
        if (React.isValidElement(child) && child.type === SelectValue) {
          return React.cloneElement(child as React.ReactElement<SelectValueProps>, {
            value,
            children: selectedText
          });
        }
        return child;
      })}
    </div>
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
      <path d="m6 9 6 6 6-6"/>
    </svg>
  </div>
);

interface SelectValueProps {
  placeholder: string;
  value?: string;
  children?: React.ReactNode;
}

export const SelectValue: React.FC<SelectValueProps> = ({ placeholder, value, children }) => {
  // If there are children, render them (for custom rendering)
  if (children) {
    return <>{children}</>;
  }
  
  // Otherwise, render the value or placeholder
  return <span className={value ? "text-gray-300" : "text-gray-500"}>{value || placeholder}</span>;
};

interface SelectContentProps {
  children: React.ReactNode;
  onSelect?: (value: string) => void;
  value?: string;
}

export const SelectContent: React.FC<SelectContentProps> = ({ children, onSelect, value }) => (
  <div className="py-1">
    {React.Children.map(children, child => {
      if (React.isValidElement(child) && child.type === SelectItem) {
        const selectItemChild = child as React.ReactElement<SelectItemProps>;
        return React.cloneElement(selectItemChild, {
          onSelect,
          isSelected: value === selectItemChild.props.value
        });
      }
      return child;
    })}
  </div>
);

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  onSelect?: (value: string) => void;
  isSelected?: boolean;
}

export const SelectItem: React.FC<SelectItemProps> = ({ value, children, onSelect, isSelected }) => (
  <div 
    className={`px-3 py-2 hover:bg-background-hover cursor-pointer text-gray-300 ${isSelected ? 'bg-primary/20' : ''}`}
    onClick={() => onSelect && onSelect(value)}
    data-value={value}
  >
    {children}
  </div>
);

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export const Dialog: React.FC<DialogProps> = ({ open, onOpenChange, children }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70" onClick={() => onOpenChange(false)}>
      <div onClick={(e) => e.stopPropagation()} className="relative z-50">
        {children}
      </div>
    </div>
  );
};

interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogContent: React.FC<DialogContentProps> = ({ children, className = '' }) => (
  <div className={`bg-background-card border border-border-dark rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] flex flex-col ${className}`}>
    {children}
  </div>
);

interface DialogHeaderProps {
  children: React.ReactNode;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({ children }) => (
  <div className="px-6 py-4 border-b border-border-dark">{children}</div>
);

interface DialogTitleProps {
  children: React.ReactNode;
}

export const DialogTitle: React.FC<DialogTitleProps> = ({ children }) => (
  <h3 className="text-lg font-semibold text-gray-200">{children}</h3>
);

interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogFooter: React.FC<DialogFooterProps> = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-t border-border-dark flex justify-end space-x-2 ${className}`}>{children}</div>
);

interface AlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export const AlertDialog: React.FC<AlertDialogProps> = ({ open, onOpenChange, children }) => {
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70" onClick={() => onOpenChange(false)}>
      <div onClick={(e) => e.stopPropagation()} className="relative z-50">
        {children}
      </div>
    </div>
  );
};

interface AlertDialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export const AlertDialogContent: React.FC<AlertDialogContentProps> = ({ children, className = '' }) => (
  <div className={`bg-background-card border border-border-dark rounded-lg shadow-xl max-w-md w-full mx-4 ${className}`}>
    {children}
  </div>
);

interface AlertDialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const AlertDialogHeader: React.FC<AlertDialogHeaderProps> = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
);

interface AlertDialogTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const AlertDialogTitle: React.FC<AlertDialogTitleProps> = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-200 ${className}`}>{children}</h3>
);

interface AlertDialogDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

export const AlertDialogDescription: React.FC<AlertDialogDescriptionProps> = ({ children, className = '' }) => (
  <p className={`mt-2 text-sm text-gray-400 ${className}`}>{children}</p>
);

interface AlertDialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const AlertDialogFooter: React.FC<AlertDialogFooterProps> = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-t border-border-dark flex justify-end space-x-2 ${className}`}>{children}</div>
);

interface AlertDialogCancelProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export const AlertDialogCancel: React.FC<AlertDialogCancelProps> = ({ children, onClick, className = '' }) => (
  <button 
    onClick={onClick}
    className={`px-4 py-2 border border-border-dark rounded-md text-sm font-medium bg-background-hover hover:bg-background-hover/80 text-gray-300 ${className}`}
  >
    {children}
  </button>
);

interface AlertDialogActionProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export const AlertDialogAction: React.FC<AlertDialogActionProps> = ({ children, onClick, className = '' }) => (
  <button 
    onClick={onClick}
    className={`px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark ${className}`}
  >
    {children}
  </button>
); 